'use client'

import { atom } from 'jotai'

import type {
  DeadmanArmed,
  DeadmanArmRefused,
  DeadmanFired,
  ServiceNotification,
  VenueCapabilities,
  VenueConnectionState,
  VenueRateLimits,
} from '@/types/generated/notifications'

export type LastDeadman =
  | { kind: 'armed'; armed: DeadmanArmed }
  | { kind: 'fired'; fired: DeadmanFired }
  | { kind: 'armRefused'; refused: DeadmanArmRefused }

export interface VenueTelemetry {
  rateLimits?: VenueRateLimits[]
  capabilities?: VenueCapabilities
  connection?: VenueConnectionState
  lastDeadman?: LastDeadman
}

export type VenueTelemetryByAccount = Record<string, VenueTelemetry>

// MM venue-layer notifications (ENG-1063), latest-only per account. Kept out of the
// notification history; cleared on logout in use-reset-client-storage.ts.
export const venueTelemetryAtom = atom<VenueTelemetryByAccount>({})

function mergeRateLimits(
  existing: VenueRateLimits[] | undefined,
  next: VenueRateLimits
): VenueRateLimits[] {
  const list = existing ?? []
  const i = list.findIndex((r) => r.scope === next.scope && r.scopeKey === next.scopeKey)
  if (i === -1) return [...list, next]
  if (Number(next.readAtMs) < Number(list[i].readAtMs)) return list
  return list.map((r, j) => (j === i ? next : r))
}

function patchFor(n: ServiceNotification, cur: VenueTelemetry): Partial<VenueTelemetry> | null {
  if (n.venueRateLimits) return { rateLimits: mergeRateLimits(cur.rateLimits, n.venueRateLimits) }
  if (n.venueCapabilities) return { capabilities: n.venueCapabilities }
  if (n.venueConnectionState) return { connection: n.venueConnectionState }
  if (n.deadmanArmed) return { lastDeadman: { kind: 'armed', armed: n.deadmanArmed } }
  if (n.deadmanFired) return { lastDeadman: { kind: 'fired', fired: n.deadmanFired } }
  if (n.deadmanArmRefused)
    return { lastDeadman: { kind: 'armRefused', refused: n.deadmanArmRefused } }
  return null
}

/** Pure reducer: folds one venue-layer notification into the per-account telemetry. */
export function applyVenueTelemetry(
  state: VenueTelemetryByAccount,
  n: ServiceNotification
): VenueTelemetryByAccount {
  const cur = state[n.accountId] ?? {}
  const patch = patchFor(n, cur)
  if (!patch) return state
  return { ...state, [n.accountId]: { ...cur, ...patch } }
}
